import type { PackageBenefit, PackagePayload } from "../api/claimsApi";

function amount(value: number, currency: string): string {
  return `${value.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} ${currency === "USD" ? "$" : currency}`;
}

const PERIOD_TEXT: Record<PackageBenefit["period"], string> = {
  year: "par an",
  years: "tous les N ans",
  pregnancy: "par grossesse",
  stay: "par séjour",
};

/** Périodicité en toutes lettres (« par an », « tous les 2 ans »). */
export function periodText(period: PackageBenefit["period"], years: number | null): string {
  return period === "years" ? `tous les ${years ?? 2} ans` : PERIOD_TEXT[period];
}

/** Taux appliqué à un acte : le sien, sinon celui de sa section, sinon 100 %. */
export function effectiveRate(benefit: PackageBenefit, sectionRate: number | null | undefined): number {
  return benefit.coverage_rate ?? sectionRate ?? 100;
}

/** Texte de la colonne « Plafond », dans les termes du plan (« 300 $ tous les 2 ans », « Sur accord préalable »). */
export function benefitCeilingText(benefit: PackageBenefit, currency: string, rate = effectiveRate(benefit, null)): string {
  if (rate <= 0) return benefit.note || "Non garantie";
  const period = periodText(benefit.period, benefit.period_years);
  const parts: string[] = [];
  if (benefit.unit_ceiling !== null) parts.push(`${amount(benefit.unit_ceiling, currency)}${benefit.unit_basis === "day" ? "/jour" : " par acte"}`);
  if (benefit.max_quantity !== null && benefit.period_ceiling !== null) {
    parts.push(`${benefit.max_quantity} max avec un plafond de ${amount(benefit.period_ceiling, currency)} ${period}`);
  } else if (benefit.max_quantity !== null) {
    parts.push(`${benefit.max_quantity} max ${period}`);
  } else if (benefit.period_ceiling !== null) {
    parts.push(`${amount(benefit.period_ceiling, currency)} ${period}`);
  }
  if (benefit.prior_approval === "always") parts.push("sur accord préalable");
  if (benefit.prior_approval === "above" && benefit.prior_approval_threshold !== null) {
    parts.push(`accord préalable à partir de ${amount(benefit.prior_approval_threshold, currency)}`);
  }
  if (benefit.age_max !== null && benefit.age_min === null) parts.push(`jusqu'à ${benefit.age_max} ans`);
  if (benefit.age_min !== null && benefit.age_max === null) parts.push(`à partir de ${benefit.age_min} ans`);
  if (benefit.age_min !== null && benefit.age_max !== null) parts.push(`de ${benefit.age_min} à ${benefit.age_max} ans`);
  const text = parts.join(", ");
  if (benefit.note && !text) return benefit.note;
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Frais réel";
}

type Props = {
  plan: PackagePayload;
  /** Affiche les codes actes et affections visés (Admin, Sinistre). */
  showCodes?: boolean;
};

/**
 * Table des bénéfices d'un package, au format d'un plan d'assureur :
 * plafonds globaux, puis par section les prestations avec couverture et plafond.
 */
export function BenefitPlanTable({ plan, showCodes = false }: Props) {
  const currency = plan.currency || "USD";

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-primary text-left text-xs uppercase text-white">
            <th className="px-3 py-2">Prestations</th>
            <th className="w-28 px-3 py-2 text-center">Couverture</th>
            <th className="px-3 py-2">Plafond</th>
          </tr>
        </thead>
        <tbody>
          {plan.coverage_zone ? (
            <tr className="border-t border-border bg-card-muted/60">
              <td className="px-3 py-2 font-semibold text-foreground">Zone de couverture</td>
              <td colSpan={2} className="px-3 py-2 text-foreground">{plan.coverage_zone}</td>
            </tr>
          ) : null}
          {plan.person_ceiling !== null || plan.family_ceiling !== null ? (
            <tr className="border-t border-border bg-card-muted/60">
              <td className="px-3 py-2 font-semibold text-foreground">Plafond global annuel</td>
              <td colSpan={2} className="px-3 py-2 font-semibold text-foreground">
                {[
                  plan.person_ceiling !== null ? `${amount(plan.person_ceiling, currency)}/personne/an` : null,
                  plan.family_ceiling !== null ? `${amount(plan.family_ceiling, currency)}/famille/an` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </td>
            </tr>
          ) : null}
          {plan.sections.map((section, sectionIndex) => (
            <SectionRows key={`${section.name}-${sectionIndex}`} section={section} currency={currency} showCodes={showCodes} />
          ))}
          {plan.sections.length === 0 ? (
            <tr>
              <td colSpan={3} className="px-3 py-4 text-center text-muted">Aucune prestation : tous les actes sont pris en charge à 100 %.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function SectionRows({ section, currency, showCodes }: { section: PackagePayload["sections"][number]; currency: string; showCodes: boolean }) {
  const extras = [
    section.ceiling_amount !== null
      ? `Max ${amount(section.ceiling_amount, currency)} ${periodText(section.ceiling_period ?? "year", section.ceiling_period_years ?? null)}`
      : null,
    section.coverage_rate !== null && section.coverage_rate !== undefined ? `couverture ${section.coverage_rate.toLocaleString("fr-FR")} %` : null,
    section.waiting_months ? `carence ${section.waiting_months} mois` : null,
  ].filter(Boolean);

  return (
    <>
      <tr className="border-t border-border bg-primary/10">
        <td colSpan={3} className="px-3 py-2">
          <span className="text-xs font-bold uppercase tracking-wide text-primary">{section.name}</span>
          {extras.length > 0 ? <span className="ml-2 text-xs font-semibold text-foreground">{extras.join(" · ")}</span> : null}
          {section.note ? <span className="block text-xs text-muted">{section.note}</span> : null}
          {section.excluded_icd_codes ? (
            <span className="block text-xs text-red-700">Affections non applicables : {section.excluded_icd_codes}</span>
          ) : null}
        </td>
      </tr>
      {section.benefits.map((benefit, index) => {
        const applied = benefit.act_codes.length > 0 || Boolean(benefit.icd_codes);
        const rate = effectiveRate(benefit, section.coverage_rate);
        return (
          <tr key={`${benefit.label}-${index}`} className="border-t border-border align-top">
            <td className="px-3 py-2 text-foreground">
              {benefit.label}
              {showCodes ? (
                <span className="mt-0.5 block text-[11px] text-muted">
                  {benefit.act_codes.length > 0 ? <span className="font-mono">{benefit.act_codes.join(", ")}</span> : null}
                  {benefit.icd_codes ? <span>{benefit.act_codes.length > 0 ? " · " : ""}CIM-10 {benefit.icd_codes}</span> : null}
                  {!applied ? <span className="italic">Mention du plan, non appliquée automatiquement</span> : null}
                </span>
              ) : null}
            </td>
            <td className={`px-3 py-2 text-center font-semibold tabular-nums ${rate <= 0 ? "text-red-700" : "text-foreground"}`}>
              {rate.toLocaleString("fr-FR")}%
            </td>
            <td className="px-3 py-2 text-foreground">{benefitCeilingText(benefit, currency, rate)}</td>
          </tr>
        );
      })}
    </>
  );
}
